import { initI18n, storedLocale } from "@dl/web-core-js/i18n";
import { AppRoot } from "@dl/web-core-js/shell";
import { ThemeProvider } from "@dl/web-core-js/theme";
import type { Brand } from "@dl/web-core-js/theme";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import { nav } from "./nav";
import { GalleryPage } from "./pages/gallery/GalleryPage";
import { router } from "./router";
import "./styles.css";

const brand: Brand = {
  name: "Logic",
  logo: "/logo.svg",
};

/** `/preview/<uuid>` is the anonymous gallery; everything else needs a session. */
const PREVIEW_PREFIX = "/preview/";

/**
 * Everything specific to this app lives above: a brand, a nav manifest and a
 * route tree. The shell, auth, theming, i18n and data layer come from
 * @dl/web-core-js and are identical across the three deployments.
 */
async function bootstrap() {
  await initI18n({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    ...(storedLocale() ? { initialLocale: storedLocale()! } : {}),
  });

  const container = document.getElementById("root");
  if (!container) throw new Error("#root is missing from index.html");

  // The gallery is mounted outside `AppRoot` on purpose: AppRoot's gate shows
  // the login screen to anonymous visitors, and this route exists precisely so
  // a buyer with no account can open it.
  const { pathname } = window.location;
  if (pathname.startsWith(PREVIEW_PREFIX)) {
    const uuid = decodeURIComponent(pathname.slice(PREVIEW_PREFIX.length).replace(/\/+$/, ""));
    createRoot(container).render(
      <StrictMode>
        <ThemeProvider defaultPreference="light">
          <GalleryPage uuid={uuid} />
        </ThemeProvider>
      </StrictMode>,
    );
    return;
  }

  createRoot(container).render(
    <StrictMode>
      <AppRoot brand={brand} nav={nav} basePath="/api/2.0" defaultTheme="light">
        <RouterProvider router={router} />
      </AppRoot>
    </StrictMode>,
  );
}

void bootstrap();
